import { createSlice } from '@reduxjs/toolkit';

// Import translation dictionaries
import enTranslations from '../../../public/locales/en/common.json';
import hiTranslations from '../../../public/locales/hi/common.json';

const dictionaries = {
  en: enTranslations,
  hi: hiTranslations
};

// Get initial language from localStorage (client-side only)
const getInitialLanguage = () => {
  // Always return 'en' during SSR to prevent hydration mismatch
  if (typeof window === 'undefined') {
    return 'en';
  }
  
  // On client-side, check localStorage
  try {
    return localStorage.getItem('LJPR_LANGUAGE') || 'en';
  } catch (error) {
    console.warn('Error reading language from localStorage:', error);
    return 'en';
  }
};

const initialLanguage = getInitialLanguage();

const initialState = {
  language: initialLanguage,
  dictionary: dictionaries[initialLanguage],
  isLoading: false,
  error: null,
  availableLanguages: [
    { code: 'en', name: 'English' },
    { code: 'hi', name: 'हिंदी' }
  ]
};

export const languageSlice = createSlice({
  name: 'language',
  initialState,
  reducers: {
    changeLanguage: (state, action) => {
      const newLanguage = action.payload;
      if (!dictionaries[newLanguage]) {
        state.error = `Language '${newLanguage}' is not supported`;
        return;
      }
      
      state.language = newLanguage;
      state.dictionary = dictionaries[newLanguage];
      state.error = null;
      
      // Store in localStorage
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('LJPR_LANGUAGE', newLanguage);
        } catch (error) {
          console.warn('Error saving language to localStorage:', error);
        }
      }
    },
    setLanguageLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setLanguageError: (state, action) => {
      state.error = action.payload;
    },
    clearLanguageError: (state) => {
      state.error = null;
    }
  },
});

// Export actions and reducer
export const { changeLanguage, setLanguageLoading, setLanguageError, clearLanguageError } = languageSlice.actions;
export default languageSlice.reducer;

// Translation helper function
export const translateText = (state, key) => {
  const keys = key.split('.');
  let value = state.language.dictionary;
  
  for (const k of keys) {
    if (value && value[k]) {
      value = value[k];
    } else {
      return key; // Return the key if translation is missing
    }
  }
  
  return value;
};
